import type { CasesLocale } from '../types'

export const enCases: CasesLocale = {
  hero: {
    tag: 'Cases · Where ontology has landed',
    title: '16 places ontology + agent has shipped',
    subtitle: 'Real customers · real before-and-after · across manufacturing, energy, government, finance, retail and R&D',
    leadParagraph:
      'Every case below is taken from a live deployment. Pain points, approach, and quantified outcomes are summarised as published. Read top-down for an overview, or jump to whichever industry / function matches what you\'re working on.',
  },
  filters: {
    industryLabel: 'Industry',
    functionLabel: 'Function',
    resultsCountTpl: 'Showing {filtered} of {total} cases',
    clearLabel: 'Clear filters',
    emptyStateTitle: 'No matching cases',
    emptyStateBody: 'Try removing some filters to widen the search.',
  },
  sectionLabels: {
    painPoints: 'Pain points · before',
    solution: 'Solution · approach',
    outcomes: 'Outcomes',
    paradigms: 'Paradigms',
    customer: 'Customer',
    metricHeader: 'Metric',
    beforeHeader: 'Before',
    afterHeader: 'After',
  },
  cases: [
    {
      id: 'quality-traceability-fast',
      industryTag: 'Manufacturing',
      functionTag: 'Quality traceability',
      paradigmTags: ['Document-driven', 'Question-grounded', 'Change-propagation'],
      customer: 'Tier-1 manufacturer at scale',
      title: 'Minute-level quality traceability',
      painPoints: [
        'A single quality incident pulled in 5 departments and dozens of records; tracing one issue took 8+ hours.',
        'Recall scope was inaccurate — too wide and unit cost ballooned; too narrow and audit flagged risk.',
        'ERP / MES / WMS / QMS use different naming standards; cross-system reconciliation done by humans.',
        'Tracing only reached batch level, never down to specific product / line / critical part.',
        'Historical traceability data sat in static archives — never fed back into prediction or risk scoring.',
      ],
      solution: [
        'Built a quality-domain Ontology: standardise material / product / work-order / supplier / work-step into one semantic model with explicit relations.',
        'Multi-source graph: auto-extract → entity-resolve → relation-fuse data from ERP / MES / WMS / QMS into a unified graph; auto-repair link breaks using flow patterns.',
        'Bi-directional traceability: forward (material → work-step → product → customer) and reverse (complaint → product → work-order → material → supplier) — any node can be the entry point.',
        'Natural-language traceability agent: "Find all products using batch 3 last week, flag the work-step with highest defect risk" — no IT team required.',
      ],
      metrics: [
        { label: 'Full-chain trace time', before: '3-7 days', after: 'A few minutes' },
        { label: 'Cross-dept follow-ups', before: 'Many calls / group chats', after: '-70%' },
        { label: 'Root-cause accuracy', before: 'Low · experience-based', after: '+30%' },
        { label: 'Compliance pass rate', before: 'Inconsistent', after: '100% stable' },
        { label: 'New-hire ramp time', before: '2-3 weeks', after: 'A few days' },
      ],
      insight:
        'Tracing changed from "string time-events together" into "one-click graph traversal" — and from "query" into "inference".',
    },
    {
      id: 'manufacturing-customer-complaint',
      industryTag: 'Manufacturing',
      functionTag: 'Customer complaint · Design iteration',
      paradigmTags: ['Multi-agent', 'Document-driven', 'Goal-driven'],
      customer: 'Manufacturing enterprise (multi-department)',
      title: 'Customer-complaint full-chain trace + design feedback',
      painPoints: [
        'A single complaint required customer manager, R&D manager, finished-goods manager and supply-chain manager to query CRM / PLM / MES / ERP separately.',
        'End-to-end trace took ~4 person-days for one complaint.',
        'Slow trace blocked R&D\'s ability to iterate designs based on complaint patterns.',
      ],
      solution: [
        'Modelled the entire complaint flow as a Domain Scenario Ontology with all key nodes captured.',
        'Three coordinated agents: Complaint-locator (from product-batch back to customer), Production-tracer (work-orders, completion records), Supply-chain-tracer (procurement, raw-material inbound).',
        'After tracing, agents drive a Product-iteration Workflow: requirements decomposition + design proposal generation.',
        'Not just query — agents can write back: generate requirement specs, design proposals, file change-requests.',
      ],
      metrics: [
        { label: 'Coordination headcount', before: 'Multi-dept, multiple people', after: '1 person driving' },
        { label: 'Time required', before: '~4 person-days', after: '5 minutes' },
        { label: 'Closed loop', before: 'Query only', after: 'Query + generate + write-back' },
      ],
      insight:
        'Same ontology that powers the trace also powers the iteration — complaint becomes a design input, not a finished file.',
    },
    {
      id: 'equipment-maintenance',
      industryTag: 'Manufacturing',
      functionTag: 'Predictive maintenance',
      paradigmTags: ['Event-driven', 'Continuous-audit', 'Explainability', 'What-if'],
      customer: 'Large manufacturer with hundreds of core production assets',
      title: 'From reactive repair to predictive maintenance',
      painPoints: [
        'Maintenance data scattered, reports manually authored, no consistent knowledge trail.',
        'Equipment health invisible; failure mechanisms hard to quantify; root-cause heavily dependent on senior experts.',
        'Repair plans were non-standardised and non-reusable.',
        'Frequent unplanned downtime → ongoing production loss and operational cost pressure.',
      ],
      solution: [
        'Ontology-based Agentic AI abstracts equipment structure, fault mechanisms, and ops flow into a computable knowledge system.',
        'Three-tier platform: Assessment (multi-dimensional health scoring, criticality grading, cluster-level co-diagnosis) → Prediction (RUL, early warnings, fault-propagation simulation) → Intervention (auto-generated repair plans, resource coordination, dynamic adjustment).',
        'Multi-agent collaboration covers report, diagnosis, and repair end-to-end; each step auto-logged.',
        'Repair completion feeds back into the knowledge base — system gets smarter with each cycle.',
      ],
      metrics: [
        { label: 'Daily / weekly report generation', before: '2-3 hours (manual)', after: 'Under 5 minutes' },
        { label: 'Detection report efficiency', before: 'Baseline', after: '+80%' },
        { label: 'Maintenance efficiency', before: 'Baseline', after: '+40%' },
        { label: 'Anomaly troubleshooting cycle', before: 'Baseline', after: '-70%' },
        { label: 'Pre-warning lead time', before: 'None', after: '2 weeks before incident (continuous caster bearing)' },
      ],
      insight:
        'Maintenance shifts from "people find a plan" to "plan finds the right people" — standardised, transparent, processed.',
    },
    {
      id: 'supply-chain-resilience',
      industryTag: 'Manufacturing · Logistics',
      functionTag: 'Supply chain · Scheduling · Risk',
      paradigmTags: ['Change-propagation', 'Multi-agent', 'Event-driven', 'What-if'],
      customer: 'Large equipment-manufacturer (raw-material procurement through delivery)',
      title: 'Visible · controllable · predictable supply chain',
      painPoints: [
        'ERP and MES disagree on what "capacity" means — manual reconciliation eats hours per scheduling cycle.',
        'Complex multi-order multi-constraint scheduling relies on expert experience; compliance risk high.',
        'Supply-chain risk intel scattered across news / business systems / handwritten notes; cannot be anticipated.',
        'Order on-time delivery only 82%; annual losses from missed events exceed $10M.',
      ],
      solution: [
        'Supply-chain domain Ontology unifies semantics across raw-material → supplier → capacity → order → scheduling → delivery.',
        'Multi-constraint smart scheduling: model breaks down capacity, lead-time, environmental compliance, supplier capacity into prioritised constraints; LLM generates an optimal schedule supporting drag-and-drop adjustments.',
        'Dynamic anomaly response: monitor demand fluctuations, equipment faults, material delays; generate and rank multiple recovery plans.',
        'Industry-chain intelligence centre: full-chain KG (material → supplier → production → order → customer → regulation) + impact-path visualisation + 72-hour lead-time risk warning.',
      ],
      metrics: [
        { label: 'Order on-time rate', before: '82%', after: '98.6%' },
        { label: 'Data accuracy', before: 'Baseline', after: '99.9%' },
        { label: 'Scheduling efficiency', before: 'Baseline', after: '+45%' },
        { label: 'Compliance coverage', before: 'Partial', after: '100%' },
        { label: 'Risk warning lead-time', before: 'Lagging', after: '72 hours ahead' },
        { label: 'Annual risk losses', before: '$10M+', after: '-$8M' },
      ],
      insight:
        'Supply chains stop reacting to events and start anticipating them — semantic alignment was the missing layer.',
    },
    {
      id: 'production-management',
      industryTag: 'Manufacturing',
      functionTag: 'Production scheduling · Project · Energy',
      paradigmTags: ['Change-propagation', 'Question-grounded', 'Continuous-audit'],
      customer: 'Large manufacturer (many systems, manual-experience scheduling)',
      title: 'Production management — from experience to data-driven',
      painPoints: [
        'Order / material / equipment / process / inventory data spread across ERP / MES / WMS; meanings of the same metric differ.',
        'Multi-constraint scheduling complexity rising; rule conflicts frequent; experience cannot weigh trade-offs globally.',
        'Project-mode production has long delivery chains; ad-hoc analytics depend on IT or data analysts.',
        'Energy management stuck at "after-the-fact statistics" — disconnected from equipment status / production rhythm / environment.',
      ],
      solution: [
        'Unified production semantic model: ontology models for order, material, equipment, process, capacity with explicit entities, attributes, relations, events.',
        'Multi-constraint smart scheduling: agent senses live state; order changes or equipment anomalies auto-trigger re-scheduling and impact assessment.',
        'Project-lifecycle smart analysis: ontology for milestones, line stations, schedule, quality; natural-language project queries.',
        'Energy-production data fusion: auto-detect non-production-period anomalies, low-efficiency equipment, with weather and other external factors integrated; closed-loop "detect → analyse → adjust → assess".',
      ],
      metrics: [
        { label: 'Cross-system data', before: 'Manual reconciliation', after: 'Auto-aligned' },
        { label: 'Scheduling generation time', before: 'Baseline', after: 'Significantly reduced' },
        { label: 'Project query response', before: 'Days', after: 'Hour-level' },
        { label: 'Energy waste detection', before: 'Hidden / un-quantified', after: 'Precisely identified · ongoing' },
      ],
      insight:
        'When the system actually understands the production logic, scheduling becomes a calculation problem instead of a negotiation problem.',
    },
    {
      id: 'rail-inspection-report',
      industryTag: 'Energy · Rail',
      functionTag: 'Reports · Compliance',
      paradigmTags: ['Document-driven', 'Goal-driven', 'Multi-agent'],
      customer: 'Energy / rail group',
      title: 'Rail inspection reports — 70× speed-up',
      painPoints: [
        'A composite rail inspection report previously required 30 people working 7 days.',
        'Manual data assembly was slow, error-prone, and quality varied with the team.',
        'Same data + similar ontology layer also needed for hazard identification, maintenance plans, emergency response — but each was built from scratch.',
      ],
      solution: [
        'Ontology-modelled equipment / defect / location / inspection-method covers the full inspection knowledge graph.',
        '3-person team spends ~1 day curating data into the ontology; agent then drafts the report in 30 minutes.',
        'Same stack scales to safety-hazard identification, maintenance planning, emergency playbook generation, knowledge-training tutoring, and resource matching.',
      ],
      metrics: [
        { label: 'Authoring effort', before: '30 people × 7 days', after: '3 people × 1 day' },
        { label: 'Drafting time', before: 'Days', after: '30 minutes (agent)' },
        { label: 'Overall efficiency', before: 'Baseline', after: '~70× speed-up' },
        { label: 'Accuracy', before: 'Manual', after: 'Higher than manual' },
      ],
      insight:
        'One ontology, many reports — invest once in the model, harvest across the full operations and compliance landscape.',
    },
    {
      id: 'energy-operations-analysis',
      industryTag: 'Energy',
      functionTag: 'Operations analytics · Decision support',
      paradigmTags: ['Question-grounded', 'Explainability', 'Multi-agent'],
      customer: 'Large energy group · executive management layer',
      title: 'Self-driving operations analysis',
      painPoints: [
        'Questions like "why are labour / freight / electricity costs up?" cannot be answered directly — six-step manual process required.',
        '6-step flow: find data → align definitions → analyse → use experience → write report → produce charts and revise multiple times.',
        'End-to-end: 5-7 days, 3-6 people per analysis.',
        'Conclusions heavily flavoured by individual experience — hard to defend at board level.',
      ],
      solution: [
        'Auto-planning agent: receives the natural-language question, builds its own analysis plan based on context engineering.',
        'Auto-discovers and unifies data sources (labour / freight / electricity / revenue / volume) across business-finance-integrated ontology.',
        'Tool-calling for computation (e.g. Code Agent) within ontology-defined business framework (cost structure / I/O / efficiency / risk).',
        'Outputs trend, structure, year-over-year, root-cause analysis with charts, and supports follow-up questions interactively.',
      ],
      metrics: [
        { label: 'Time required', before: '5-7 days', after: '30 minutes' },
        { label: 'People involved', before: '3-6 people', after: '1 person driving' },
        { label: 'Objectivity', before: 'Experience-coloured', after: 'Ontology-traceable' },
      ],
      insight:
        'Decision support stops being a report-authoring marathon and becomes a conversation — at the speed of curiosity, not the speed of process.',
    },
    {
      id: 'finance-investor-disclosure',
      industryTag: 'Finance',
      functionTag: 'Regulatory · Disclosure · Form-filling',
      paradigmTags: ['Document-driven', 'Change-propagation'],
      customer: 'Financial investment institution (multi-LP scenario)',
      title: 'LP disclosure auto-fill — one ontology, every template',
      painPoints: [
        'Different LPs require different disclosure templates.',
        'Traditional approach: build a dedicated agent for every template — slow, expensive, hard to iterate.',
      ],
      solution: [
        'Users simply upload the target template.',
        'System combines the template with the ontology platform to auto-fill data — no need to describe intent.',
        'Template changes affect only the "data-retrieval path", not the underlying data ontology.',
      ],
      metrics: [
        { label: 'New LP template', before: 'Build a dedicated agent', after: 'Drop in, auto-fill' },
        { label: 'Disclosure response', before: 'Slow + inconsistent', after: 'Fast + consistent' },
      ],
      insight:
        'When the data ontology is solid, every new reporting format becomes a lookup — not a re-build.',
    },
    {
      id: 'led-line-quality',
      industryTag: 'Manufacturing · Electronics',
      functionTag: 'Quality + task dispatch',
      paradigmTags: ['Multi-agent', 'Event-driven', 'Change-propagation'],
      customer: 'LED production line',
      title: 'LED production line — autonomous Orion AI agent',
      painPoints: [
        'Quality alerts trigger downstream actions across supplier, line, and customer — but coordination was fragmented.',
        'Each role had its own tool / channel; alerts didn\'t auto-propagate into actionable tasks.',
      ],
      solution: [
        'The Orion AI agent autonomously calls "quality-traceability" and "task-dispatch" Onto-Skills.',
        'On a warning-ticket, it generates an improvement report.',
        'Differentiated tasks dispatched to: supplier lead, line lead, and customer assistant (an agent deployed in the team chat workspace).',
        'End-to-end: from problem detection → traced report → coordinated dispatch — fully automated.',
      ],
      metrics: [
        { label: 'Detection → task dispatch', before: 'Manual fan-out', after: 'Automated, role-aware' },
        { label: 'Coordination loop', before: 'Multi-tool, multi-channel', after: 'Single ontology-driven flow' },
      ],
      insight:
        'When the ontology speaks "supplier · line · customer" natively, the agent simply routes — no integration glue required.',
    },
    {
      id: 'ceramic-bearing-design',
      industryTag: 'Manufacturing',
      functionTag: 'R&D · Design → production loop',
      paradigmTags: ['Goal-driven', 'Change-propagation', 'Multi-agent'],
      customer: 'Industrial bearing design',
      title: 'Ceramic-composite bearing — design to procurement in one chain',
      painPoints: [
        'Bearing R&D output (material composition + structural params + performance + scenario fit) historically required manual handoff to PDM, ERP, and MES.',
        'Material procurement triggered by humans noticing a need — slow and prone to forgetting.',
        'Constraint validation (heat tolerance, fatigue cycles) done late, expensively, sometimes after the fact.',
      ],
      solution: [
        'Ontology defines "design outcome" with entities (composition / structure / performance / scenario) and explicit relations.',
        'Agent auto-decomposes the design: SOLIDWORKS parametric files (e.g. ceramic-grain 35%, cage hollow-out 20%), BOM list (via "bearing type → material code" mapping), constraint sheet (heat ≥ 180℃, fatigue cycles > 1M).',
        'Cross-system propagation: drawings + version notes → PDM; BOM → ERP; process change ticket → MES; procurement order (e.g. 3 tons nano-silicon nitride to vendor A) → procurement system.',
        'Human-in-loop on threshold breaches: ceramic-material cost > 10% over budget → human approval with cost-vs-performance trade-off report.',
      ],
      metrics: [
        { label: 'Design → procurement handoff', before: 'Manual / sequential', after: 'Auto-propagated' },
        { label: 'Material constraint validation', before: 'Late / costly', after: 'At design time' },
        { label: 'Cost-overrun review', before: 'Reactive', after: 'Auto-routed with decision report' },
      ],
      insight:
        'Design and production stop being two silos with handoffs — they share one knowledge fabric.',
    },
    {
      id: 'city-service-hotline',
      industryTag: 'Government',
      functionTag: 'Customer service · Risk · Data analytics',
      paradigmTags: ['Question-grounded', 'Document-driven', 'Event-driven', 'Continuous-audit'],
      customer: 'Major-city government services hotline (100 agents)',
      title: 'City services hotline — smarter on every dimension',
      painPoints: [
        'Agents\' knowledge varied widely; ticket dispatch error-prone on diverse citizen requests.',
        'Long queues and long handling times; direct-resolution rate only 54.25%.',
        'Reports authored manually by analysts — days to produce.',
        'Risk-perception thin across 8,000+ daily calls; potential hazards not surfaced.',
      ],
      solution: [
        'Real-time history match: model auto-retrieves similar past best-practice while agent is on call.',
        'Emotion recognition + auto ticket-summary + auto-classification + receiving-unit recommendation + auto-reply + one-click ticket creation.',
        'Smart NL Q&A over hotline data: analyst asks in natural language, gets data + text insight + charts.',
        'Daily city-wide risk pattern auto-report: high-risk events, hot events, hourly trends, working priorities — generated by template + LLM in minutes.',
      ],
      metrics: [
        { label: 'Service-agent assistant coverage', before: '0', after: '100 agents (full)' },
        { label: 'Queue wait time', before: '17 sec', after: '11 sec' },
        { label: 'Service handling time', before: 'Baseline', after: '-30%' },
        { label: 'Direct-resolution rate', before: '54.25%', after: '77.6%' },
        { label: 'Report authoring', before: 'Days', after: 'Minutes' },
      ],
      insight:
        'Three layers (agent · analyst · city operator) work on one ontology — the same data delivers service quality, decision insight, and risk perception.',
    },
    {
      id: 'asset-management-soe',
      industryTag: 'Government · Real estate',
      functionTag: 'Asset mgmt · Site selection · Idle-asset revitalisation',
      paradigmTags: ['Question-grounded', 'Document-driven', 'Risk-scoring'],
      customer: 'Large public-sector enterprise managing extensive land assets',
      title: 'Land-asset revitalisation — wake up sleeping assets',
      painPoints: [
        'Asset data scattered across asset mgmt / finance / lease info systems; no unified dynamic view.',
        'Cross-region / cross-type (commercial / industrial / residential) utilisation comparison difficult.',
        'Cannot quickly locate underperforming assets or diagnose causes (title disputes / poor location).',
        'No smart site-selection tool for matching projects to area / transport / facility combo requirements.',
        'No best-practice library of comparable land-intensive-use cases; policy tools (FAR incentives, mixed-use) under-applied.',
      ],
      solution: [
        'Asset-information management system: integrate asset data + lease + contract + finance into one dynamic view; cross-region cross-type comparison surfaces underperformers and risk hotspots.',
        'Smart Q&A assistant: NL queries on asset info, revitalisation strategy, site-selection advice; complex problems handled via follow-up + correlation across data sources.',
        'Best-practice case library integrated — query similar successful projects for local reference.',
        'Smart report generation: one-click monthly asset dynamics, idle-asset analysis, revitalisation effect, site-selection recommendations — with map-linked visualisations.',
      ],
      metrics: [
        { label: 'Data integration', before: 'Manual cross-system', after: 'Unified dynamic view' },
        { label: 'Underperforming asset diagnosis', before: 'Slow / partial', after: 'Auto + cause-identified' },
        { label: 'Site-selection support', before: 'No tool', after: 'NL-driven recommendation' },
        { label: 'Management mode', before: 'Experience-driven', after: 'Data-driven' },
      ],
      insight:
        'Idle assets aren\'t a real-estate problem — they\'re a knowledge-graph problem. Connect what already exists, and the next decision becomes obvious.',
    },
    {
      id: 'consumer-sales-production-sync',
      industryTag: 'Manufacturing · Retail',
      functionTag: 'Sales forecasting · Production sync · Inventory',
      paradigmTags: ['What-if', 'Goal-driven', 'Change-propagation'],
      customer: 'Top consumer-goods manufacturer (large SKU base, complex channel hierarchy)',
      title: 'Sales-production sync — from negotiation to calculation',
      painPoints: [
        'Sales forecasting depends on experience; data scattered; peak-season forecast deviation especially high.',
        'Production planning lags market by half a step — either supply shortfall, or inventory pressure.',
        'Decision-makers hold piles of reports but lack a clear judgement on trend / capacity match / strategy adjustment.',
      ],
      solution: [
        'Composite stack: Ontology semantic system + LLM + multi-agent collaboration.',
        'Sales-and-production forecasting agent on Orion AI: integrate historical sales + industry trend + upstream-downstream dynamics + policy direction into unified semantic view; every input traceable.',
        'Multi-granular forecasting (product / series / channel) with capacity / labour / material / WIP constraints baked in; auto-generate flexible production schedule.',
        'Output is rich: charts, tables, multi-format export, reasoning-chain visualisation — board sees not just the answer but why.',
      ],
      metrics: [
        { label: 'Sales forecast accuracy', before: 'Baseline', after: '+~20% overall' },
        { label: 'Inventory occupancy rate', before: 'Baseline', after: '-~15%' },
        { label: 'Market response cycle', before: 'Weeks', after: 'Hourly dynamic adjustment' },
        { label: 'Cross-dept communication', before: 'Arguing over numbers', after: 'Aligning on strategy' },
      ],
      insight:
        'Production-sales planning stops being a quarterly debate and becomes a daily calculation — with the reasoning visible to every decision-maker.',
    },
    {
      id: 'consumer-customer-service',
      industryTag: 'Automotive · Retail',
      functionTag: 'Customer service · Business insight',
      paradigmTags: ['Question-grounded', 'Document-driven', 'Change-propagation'],
      customer: 'Domestic e-bike manufacturer (20+ series, dozens of models)',
      title: 'E-bike customer service — service-data feeds business decisions',
      painPoints: [
        'Multiple product series and models are highly similar — systems confused user queries between them.',
        'Users phrase questions colloquially: "How\'s the range on this one?", "How does it compare to my old one?"',
        'Structured (specs) and unstructured (manuals) data lack cross-modal integration.',
        'Customer-service data only used for single response — sales / management can\'t use it for trend judgement.',
      ],
      solution: [
        'Structured + unstructured knowledge base: structured data via Embedding + index + retrieval; manuals / fault-guides via OCR + chart-recognition + layout-restoration.',
        'Knowledge graph + LLM with clear product hierarchy (category / series / model) — LLM works inside semantic constraints, no more wild guessing.',
        'Customer-service / online-business pipeline: agent can invoke vehicle repair, parts purchase, etc., not just answer.',
        'Service data feeds business: sales asks "which models had rising inquiry but no sales lift?" — and gets it in NL.',
      ],
      metrics: [
        { label: 'Query understanding', before: 'Confused across models', after: 'Disambiguated via hierarchy' },
        { label: 'Service to business loop', before: 'Closed at response', after: 'Sales / mgmt query the same data' },
        { label: 'Cross-modal data', before: 'Siloed', after: 'Unified' },
        { label: 'Insight example', before: '—', after: 'Range-spec misunderstanding identified as conversion gap; messaging adjusted' },
      ],
      insight:
        'Customer service becomes a sensor for the business — every interaction adds signal that sales and management can query later.',
    },
    {
      id: 'soe-project-management',
      industryTag: 'Government',
      functionTag: 'Project mgmt · Data Q&A',
      paradigmTags: ['Question-grounded', 'Explainability'],
      customer: 'Large enterprise group',
      title: 'AI Q&A over hundreds of thousands of projects',
      painPoints: [
        '100+ project attributes, 1,000+ KPIs, covering hundreds of thousands of projects.',
        'Traditional data queries depend on IT-built reports / dashboards — inflexible.',
        'Query results are bare numbers — no business context, no causal explanation.',
        'User questions imprecise: project names use nicknames or typos; some indicators need real-time computation; ambiguous queries ("who\'s in charge of project X?" — operations / procurement / engineering / implementation manager?).',
      ],
      solution: [
        'KG-driven knowledge structure for industry terms / data / business systems.',
        'LLM + GraphRAG graph-augmented retrieval: strong intent understanding + business alignment + query generalisation + result convergence.',
        'Active clarification of ambiguous intent — system asks before answering when needed.',
        'Multi-format result presentation (text / table / chart / graph) + industry-context interpretation = "ask = insight".',
      ],
      metrics: [
        { label: 'Coverage', before: 'Static reports', after: 'Hundreds of thousands of projects' },
        { label: 'Indicators', before: 'Fixed', after: '1,000+ via NL' },
        { label: 'Authoring path', before: 'Reports + driver views', after: 'Self-serve NL Q&A' },
        { label: 'Reuse', before: 'Project-only', after: 'Asset / contract / sales / HR / finance' },
      ],
      insight:
        'When the ontology models the org\'s vocabulary, business users stop waiting on data engineers and start having conversations with the data.',
    },
    {
      id: 'research-direction-discovery',
      industryTag: 'R&D · Industrial research',
      functionTag: 'Strategy · Tech intelligence',
      paradigmTags: ['Document-driven', 'Goal-driven', 'Continuous-audit'],
      customer: 'Industrial research institute (national-strategic mission)',
      title: 'Mapping the tech landscape · finding the choke-points',
      painPoints: [
        'Incomplete view of the technology / product chain; cannot systematically map relations across the full industry chain.',
        'Internal + external intelligence fragmented; lacks timely, comprehensive sci-tech intelligence management.',
        'Project positioning unclear — hard to judge core value and market position.',
        'Risk of wasted R&D budget on saturated or non-critical fronts; risk of missing truly forward-looking opportunities.',
      ],
      solution: [
        'Technology-product spectrum management: the Orion AI knowledge graph models the full industry chain — processes, techniques, products, R&D directions — with hierarchy and relations.',
        'Sci-tech intelligence library: unified ingestion of reports, research, papers, news, competitor moves; NL retrieval; LLM auto-extracts value; KG auto-links to relevant processes / techniques / products / projects.',
        'Dynamic R&D-direction evaluation: extract key indicators from massive internal + external intelligence to back decisions; evaluation becomes scientific and systematic.',
        'Auto-push of high-value insights to relevant project members — trends, frontier tech, opportunities reach the right people without manual curation.',
      ],
      metrics: [
        { label: 'Sci-tech papers processed', before: '—', after: '150,000+' },
        { label: 'News articles processed', before: '—', after: '100,000+' },
        { label: 'Key tags recommended', before: '—', after: '1,000+' },
        { label: 'Frontier technologies identified', before: '—', after: '142' },
        { label: 'Long-pole strengths identified', before: '—', after: '225' },
        { label: 'Choke-point technologies identified', before: '—', after: '19' },
      ],
      insight:
        'Strategic decisions go from "expert intuition" to "evidence-backed evaluation" — and the same engine then keeps watching as the field moves.',
    },
  ],
  playbookCta: {
    tag: 'Operational guide',
    title: 'How to ship one of these',
    body: 'These cases share a small set of patterns: how to start, how to model, how to control behaviour. The Playbook has all of them — 11 sections, no fluff.',
    cta: 'Open the Playbook →',
  },
}
